import { useState } from 'preact/hooks';
import {
  PERK_SLOTS,
  UNLOCKS,
  isPvpMode,
  isUnlocked,
  levelFromXp,
  profileContext,
  requirementLabel,
  totalStars,
  type UnlockDef,
  type UnlockKind,
} from '@cratered/shared';
import { lookFor } from '../game/look';
import { TEAM_COLORS } from '../game/render';
import { lobby, me, net, profile, roles, showToast } from '../store';
import { MoleCanvas } from './common';
import { AdminPanel, CreatorPanel } from './Creator';

/** Locker tabs: the unlock kinds plus, for the right people, custom art and role admin. */
type LockerTab = UnlockKind | 'custom' | 'admin';

const KIND_LABEL: Record<LockerTab, string> = { body: 'Body', outfit: 'Outfit', trail: 'Trail', perk: 'Perks', custom: 'Custom art', admin: 'Admin' };
const TRAIL_SWATCH: Record<string, string> = {
  'trail.none': 'transparent',
  'trail.dust': 'linear-gradient(90deg, transparent, #b08a60)',
  'trail.sparks': 'linear-gradient(90deg, transparent, #ffd166, #ff8a2b)',
  'trail.rainbow': 'linear-gradient(90deg, #ff5d5d, #ffcc4d, #58c977, #4fc3d9, #a78bfa)',
  'trail.fire': 'linear-gradient(90deg, transparent, #ff6a1a, #ffc43d)',
  'trail.glitch': 'repeating-linear-gradient(90deg, #39ff14 0 6px, transparent 6px 10px, #ff2bd6 10px 14px, transparent 14px 20px)',
  'trail.comet': 'linear-gradient(90deg, transparent, #8cdcff)',
  'trail.tsunami': 'linear-gradient(90deg, transparent, #50aaff)',
};

export function Locker() {
  const p = profile.value;
  const [kind, setKind] = useState<LockerTab>('body');
  if (!p) return null;
  const ctx = profileContext(p);
  const info = levelFromXp(p.xp);
  const stars = totalStars(p.stats);
  const list = UNLOCKS.filter((u) => u.kind === kind);
  // Perks sit out of every PvP mode (versus and brawl), not just versus.
  const lobbyMode = lobby.value?.settings.mode;
  const inVersusLobby = lobbyMode !== undefined && isPvpMode(lobbyMode);
  const pvpModeName = lobbyMode === 'brawl' ? 'Brawl' : 'Versus';
  const teamColor = TEAM_COLORS[(me.value?.team ?? 0) % TEAM_COLORS.length];
  const myLook = lookFor(p.equipped, teamColor);
  const tabs: LockerTab[] = ['body', 'outfit', 'trail', 'perk'];
  if (roles.value.creator) tabs.push('custom');
  if (roles.value.admin) tabs.push('admin');

  const onPick = (u: UnlockDef) => {
    const unlocked = isUnlocked(u, ctx);
    if (!unlocked) {
      showToast(`Locked — ${requirementLabel(u.requirement)}`);
      return;
    }
    if (u.kind === 'perk') {
      const has = p.perks.includes(u.id);
      const next = has ? p.perks.filter((id) => id !== u.id) : [...p.perks, u.id];
      if (next.length > PERK_SLOTS) {
        showToast(`You can carry ${PERK_SLOTS} perks. Unequip one first.`);
        return;
      }
      net.value?.send({ t: 'setPerks', perks: next });
    } else {
      net.value?.send({ t: 'equip', kind: u.kind, id: u.id });
    }
  };

  return (
    <div class="screen">
      <div class="locker">
        <section class="panel level-card">
          <div class="level-badge num" aria-label={`Level ${info.level}`}>
            {info.level}
          </div>
          <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <h2>Level {info.level}</h2>
              <span class="muted num">
                {info.xpForNext === 0 ? 'Max level' : `${info.xpIntoLevel.toLocaleString()} / ${info.xpForNext.toLocaleString()} XP`}
              </span>
            </div>
            <div class="xp-bar" role="progressbar" aria-valuenow={Math.round(info.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
              <span style={{ width: `${info.progress * 100}%` }} />
            </div>
            <div class="muted num" style={{ fontSize: 13, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>{p.stats.wins} wins</span>
              <span>{p.stats.kills} kills</span>
              <span>{p.stats.headshots} headshots</span>
              <span>{stars} campaign stars</span>
            </div>
          </div>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 220px', gap: 16, alignItems: 'start' }} class="locker-main">
          <div style={{ display: 'grid', gap: 12 }}>
            <div class="filters" role="tablist">
              {tabs.map((k) => (
                <button key={k} class="filter-chip" role="tab" aria-pressed={kind === k} onClick={() => setKind(k)}>
                  {KIND_LABEL[k]}
                  {k === 'perk' && <span class="muted"> · PvE</span>}
                </button>
              ))}
            </div>
            {kind === 'perk' && (
              <p class="muted" style={{ fontSize: 13 }}>
                Perks change gameplay, so they only work in Co-op Siege and Solo Campaign. {inVersusLobby && `Your lobby is set to ${pvpModeName} right now; these will sit out.`}
              </p>
            )}
            {kind === 'custom' && <CreatorPanel />}
            {kind === 'admin' && <AdminPanel />}
            <div class="card-grid">
              {list.map((u) => {
                const unlocked = isUnlocked(u, ctx);
                const equipped = u.kind === 'perk' ? p.perks.includes(u.id) : p.equipped[u.kind] === u.id;
                return (
                  <button
                    key={u.id}
                    class={`unlock-card${equipped ? ' equipped' : ''}${unlocked ? '' : ' locked'}`}
                    onClick={() => onPick(u)}
                    aria-pressed={equipped}
                    aria-label={`${u.name}${unlocked ? '' : `, locked, ${requirementLabel(u.requirement)}`}`}
                  >
                    <div class="card-art">
                      {u.kind === 'body' || u.kind === 'outfit' ? (
                        <MoleCanvas
                          look={{
                            ...myLook,
                            body: u.kind === 'body' ? u.id : p.equipped.body,
                            outfit: u.kind === 'outfit' ? u.id : p.equipped.outfit,
                          }}
                        />
                      ) : u.kind === 'trail' ? (
                        <div style={{ width: '80%', height: 10, borderRadius: 5, background: TRAIL_SWATCH[u.id] ?? 'transparent', boxShadow: u.id === 'trail.none' ? 'inset 0 0 0 1px var(--line-strong)' : 'none' }} />
                      ) : (
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--warn)' }}>★</span>
                      )}
                    </div>
                    <div class="card-name">{u.name}</div>
                    <div class="card-desc">{u.description}</div>
                    {u.pveOnly && <span class="lock-badge pve">PvE</span>}
                    {!unlocked && <span class="lock-badge">🔒 {requirementLabel(u.requirement)}</span>}
                    {unlocked && equipped && <span class="equipped-badge">Equipped</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <aside class="panel" style={{ position: 'sticky', top: 0 }}>
            <div class="panel-head">
              <h3>Your mole</h3>
            </div>
            <div class="panel-body">
              <div class="preview-stage" style={{ minHeight: 180 }}>
                <MoleCanvas look={myLook} aim big />
              </div>
              <p class="eyebrow">Perks · {p.perks.length}/{PERK_SLOTS}</p>
              {p.perks.length === 0 ? (
                <p class="muted" style={{ fontSize: 13 }}>None equipped.</p>
              ) : (
                p.perks.map((id) => {
                  const d = UNLOCKS.find((u) => u.id === id);
                  return d ? (
                    <div key={id} class="setting">
                      <label>
                        {d.name}
                        <span class="hint">{d.description}</span>
                      </label>
                    </div>
                  ) : null;
                })
              )}
            </div>
          </aside>
        </div>
      </div>
      <style>{`@media (max-width: 800px) { .locker-main { grid-template-columns: 1fr !important; } .locker-main aside { position: static !important; } }`}</style>
    </div>
  );
}
